import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronDown, Github, Linkedin, Mail } from 'lucide-react';
import NameDisplay from './NameDisplay';
import ProfileImage from './ProfileImage';

const Hero: React.FC = () => {
  const scrollToAbout = () => {
    const element = document.querySelector('#about');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative flex min-h-screen items-center overflow-hidden pt-24">
      <div className="section-shell relative z-10 py-12 sm:py-16">
        <div className="grid items-center gap-16 lg:grid-cols-[0.95fr_1.05fr]">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-10 text-left"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12, duration: 0.7 }}
              className="section-kicker"
            >
              AI Engineer / Full-Stack Developer
            </motion.div>

            {/* Reduced copy and stronger hierarchy keep the hero intentional and premium */}
            <div className="space-y-5">
              <NameDisplay />
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                className="max-w-3xl text-balance font-display text-5xl font-bold tracking-[-0.04em] text-white sm:text-6xl lg:text-7xl"
              >
                Engineering intelligent products.
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.34, duration: 0.75 }}
                className="max-w-xl text-balance text-base leading-7 text-slate-500 dark:text-slate-400 sm:text-lg"
              >
                I build systems where AI, software, and product thinking move as one.
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.46, duration: 0.8 }}
              className="flex flex-col gap-4 sm:flex-row"
            >
              <motion.button
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToAbout}
                className="btn-primary"
              >
                View selected work
                <ArrowRight size={18} />
              </motion.button>

              <motion.a
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                href="mailto:shivanshmane2004@gmail.com"
                className="btn-secondary"
              >
                Say hello
                <Mail size={18} />
              </motion.a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.58, duration: 0.8 }}
              className="flex items-center gap-3 pt-1"
            >
              {[
                { icon: Github, href: 'https://github.com/ShivanshMane', label: 'GitHub' },
                { icon: Linkedin, href: 'https://linkedin.com/in/shivanshmane1122', label: 'LinkedIn' },
                { icon: Mail, href: 'mailto:shivanshmane2004@gmail.com', label: 'Email' },
              ].map((social, index) => (
                <motion.a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.7 + index * 0.08, duration: 0.45 }}
                  whileHover={{ scale: 1.04, y: -2 }}
                  whileTap={{ scale: 0.9 }}
                  className="glass flex h-11 w-11 items-center justify-center rounded-full border border-white/10 text-slate-700 transition-all duration-300 hover:border-cosmic-aurora-emerald/50 hover:text-slate-900 hover:shadow-aurora-emerald dark:text-slate-300 dark:hover:text-white"
                  aria-label={social.label}
                >
                  <social.icon size={24} />
                </motion.a>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="flex justify-center lg:justify-end"
          >
            <ProfileImage />
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="cursor-pointer text-cosmic-aurora-emerald"
          onClick={scrollToAbout}
        >
          <ChevronDown size={32} />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
